import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function PPFServiceSection() {
  const videoRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const whatsappMessage = encodeURIComponent('Hola, me gustaría consultar sobre Paint Protection Film (PPF)');
  const whatsappLink = `https://wa.me/18096318952?text=${whatsappMessage}`;

  const benefits = [
    {
      id: 'ppf_benefit_1',
      icon: 'ShieldCheckIcon',
      title: 'Blindaje Avanzado',
      description: 'Protección contra impactos de piedras, abrasión y rayones profundos'
    },
    {
      id: 'ppf_benefit_2',
      icon: 'SparklesIcon',
      title: 'Autocuración',
      description: 'Autocuración con calor: rayones menores desaparecen automáticamente'
    },
    {
      id: 'ppf_benefit_3',
      icon: 'SunIcon',
      title: '99% Claridad Óptica',
      description: 'Transparencia total que preserva el acabado original de fábrica'
    },
    {
      id: 'ppf_benefit_4',
      icon: 'DocumentCheckIcon',
      title: 'Resistencia a Manchas',
      description: 'Repele contaminantes, insectos y químicos del camino'
    }
  ];

  const brands = [
    {
      id: 'onyx_vunyx',
      name: 'ONYX Vunyx®',
      logo: '/assets/images/onyx_vunyx_logo.png',
      options: [
        { name: 'Master Pro', warranty: 'Garantía de por vida', tech: 'Línea profesional premium' },
        { name: 'Shield Pro Plus', warranty: '7 años', tech: 'Tecnología TPU alemana' },
        { name: 'Shield Pro', warranty: '5 años', tech: 'Protección estándar superior' }
      ]
    },
    {
      id: 'xpel',
      name: 'XPEL',
      logo: '/assets/images/xpel_logo.png',
      options: [
        { name: 'ULTIMATE PLUS', warranty: '10 años', tech: 'Acabado brillante 7.6 mil' },
        { name: 'STEALTH', warranty: '10 años', tech: 'Acabado mate/satinado premium' }
      ]
    },
    {
      id: '3m',
      name: '3M Scotchgard™',
      logo: '/assets/images/three_m_scotchgard_logo.png',
      options: [
        { name: 'Pro Series', warranty: '10 años', tech: 'Claridad óptica superior' }
      ]
    },
    {
      id: 'suntek',
      name: 'SunTek Ultra',
      logo: '/assets/images/suntek_logo.png',
      options: [
        { name: 'HydroResist', warranty: '10 años', tech: 'Tecnología hidrofóbica avanzada' }
      ]
    }
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Lazy load: only inject iframe src when container enters viewport
    const lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoSrc) {
            setVideoSrc('https://player.vimeo.com/video/1164579842?autoplay=1&loop=1&muted=1&background=1&controls=0');
          }
        });
      },
      { rootMargin: '200px', threshold: 0 }
    );

    lazyObserver.observe(container);
    return () => lazyObserver.disconnect();
  }, [videoSrc]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoSrc) return;

    // Audio control: unmute when 50% visible
    const audioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
          } else {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
          }
        });
      },
      { threshold: 0.5 }
    );

    audioObserver.observe(videoElement);
    return () => audioObserver.disconnect();
  }, [videoSrc]);

  return (
    <section className="py-32 px-6 lg:px-12 bg-black" id="ppf">
      <div className="max-w-[1600px] mx-auto">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <span className="text-primary font-heading text-sm tracking-[0.3em] mb-4 block">
              01 / PAINT PROTECTION FILM (PPF)
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-white mb-6 tracking-tight">
              PROTECCIÓN INVISIBLE
              <br />
              <span className="text-gradient-red">DE ALTO RENDIMIENTO</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Blindaje avanzado contra impactos de piedras, abrasión y rayones. Trabajamos con las mejores marcas globales para asegurar la integridad de la pintura original.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-primary font-heading text-sm tracking-wider">
                  SELF-HEALING
                </span>
              </div>
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-white/80 font-heading text-sm tracking-wider">
                  99% CLARIDAD ÓPTICA
                </span>
              </div>
            </div>
          </div>

          <div className="relative flex justify-center">
            <div className="w-full max-w-md" ref={containerRef}>
              {/* Mobile: 16:9, Desktop: 9:16 — fixed aspect ratio prevents layout shift */}
              <div className="aspect-[16/9] md:aspect-[9/16] rounded-[32px] overflow-hidden relative shadow-[0_20px_60px_rgba(220,38,38,0.3),0_0_80px_rgba(220,38,38,0.15)] border-2 border-primary/40 hover:border-primary/60 hover:shadow-[0_25px_70px_rgba(220,38,38,0.4),0_0_100px_rgba(220,38,38,0.2)] transition-all duration-500 hover:scale-[1.02] bg-gradient-to-br from-primary/5 to-transparent">
                {/* Poster placeholder shown until video loads */}
                {!isLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-black via-zinc-900 to-black">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto mb-3 animate-pulse">
                        <svg className="w-7 h-7 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-white/40 text-xs font-heading tracking-wider">PPF PROTECTION</p>
                    </div>
                  </div>
                )}
                {videoSrc && (
                  <iframe
                    ref={videoRef}
                    src={videoSrc}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="PPF Protection Demo"
                    onLoad={() => setIsLoaded(true)}
                  />
                )}
              </div>
            </div>
            <div className="absolute -bottom-8 -right-8 glass-card p-8 rounded-[32px] max-w-xs">
              <div className="text-primary font-heading text-4xl mb-2">10</div>
              <div className="text-white/80 text-sm font-medium">
                Años de garantía en marcas premium
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit) => (
            <div
              key={benefit.id}
              className="glass-card p-6 rounded-[24px] hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                <Icon
                  name={benefit.icon as any}
                  size={24}
                  variant="outline"
                  className="text-primary"
                />
              </div>
              <h3 className="font-heading text-white text-sm tracking-wider mb-2">
                {benefit.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Brands & Options */}
        <div>
          <h3 className="font-heading text-2xl md:text-3xl text-white text-center mb-12 tracking-wider">
            MARCAS Y OPCIONES PREMIUM
          </h3>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="glass-card p-8 rounded-[32px] hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 bg-white/5 rounded-xl flex items-center justify-center p-3">
                    <AppImage
                      src={brand.logo}
                      alt={`${brand.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h4 className="font-heading text-2xl text-white tracking-wider">
                    {brand.name}
                  </h4>
                </div>
                <div className="space-y-4">
                  {brand.options.map((option, idx) => (
                    <div
                      key={`${brand.id}_option_${idx}`}
                      className="border-l-2 border-primary pl-4"
                    >
                      <div className="flex items-start justify-between mb-1">
                        <h5 className="text-white font-heading text-sm tracking-wider">
                          {option.name}
                        </h5>
                        <span className="text-primary text-xs font-medium whitespace-nowrap ml-2">
                          {option.warranty}
                        </span>
                      </div>
                      <p className="text-white/60 text-xs">{option.tech}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Key Features */}
          <div className="glass-card p-8 rounded-[32px] mb-8">
            <h4 className="font-heading text-xl text-white mb-6 tracking-wider text-center">
              CARACTERÍSTICAS CLAVE
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">Autocuración (Self-Healing)</div>
                  <div className="text-white/60 text-xs">Rayones menores desaparecen con calor</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">Resistencia a Manchas</div>
                  <div className="text-white/60 text-xs">Repele contaminantes y químicos</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">99% Claridad Óptica</div>
                  <div className="text-white/60 text-xs">Transparencia total sin distorsión</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:bg-primary/90 text-white px-12 py-5 rounded-full font-heading text-sm tracking-wider transition-all duration-300 hover:scale-105"
            >
              CONSULTAR AHORA
            </a>
            <p className="text-white/50 text-xs mt-4">Cotización personalizada vía WhatsApp</p>
          </div>
        </div>
      </div>
    </section>
  );
}